import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Input,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  useVideoConsultRequests,
  useVideoSessions,
} from '@/features/video-consults/api';
import {
  BLOCKER_LABEL,
  OUTCOME_LABEL,
  PAYMENT_LABEL,
  STATUS_LABEL,
  accessDisabledReason,
  joinDisabledReason,
  money,
  readyText,
} from '@/features/video-consults/format';
import type {
  StaffVideoConsultRequest,
  StaffVideoSession,
} from '@/features/video-consults/types';
import { PageContainer } from '@/layouts/PageContainer';
import { klTime, todayKl } from '@/pages/calendar/time';
import parity from '@/pages/ops/parity.module.css';
import { PayLaterButton } from './PayLaterButton';
import { AcceptRequestDialog, DeclineRequestDialog } from './RequestDialogs';
import { TriageSummary } from './TriageSummary';
import { VideoCallView } from './VideoCallView';
import styles from './video.module.css';

const TYPE_LABEL = {
  follow_up: 'Follow-up',
  first_visit: 'First visit by video',
} as const;

/**
 * RL-1142 Video consults (doctor): today's video sessions and the requests
 * patients started in the app. "Join" opens the call screen (?session=id),
 * whose GetStream part is lazy-loaded.
 */
export function VideoConsultsPage() {
  const [params, setParams] = useSearchParams();
  const sessionId = params.get('session');
  if (sessionId)
    return (
      <PageContainer>
        <VideoCallView id={sessionId} onBack={() => setParams({})} />
      </PageContainer>
    );
  return (
    <PageContainer>
      <SessionsCard onJoin={(id) => setParams({ session: id })} />
      <RequestsCard />
    </PageContainer>
  );
}

function SessionStatus({ s }: { s: StaffVideoSession }) {
  const st = STATUS_LABEL[s.status];
  return (
    <>
      <Badge tone={st.tone} dot>
        {st.text}
      </Badge>
      {s.status === 'waiting' && s.waitingRoom.since && (
        <span className={parity.sub}>
          Since {klTime(s.waitingRoom.since)} · camera{' '}
          {readyText(s.waitingRoom.cameraReady)} · microphone{' '}
          {readyText(s.waitingRoom.microphoneReady)}
        </span>
      )}
      {s.rejoinBy && (
        <span className={parity.sub}>
          Patient left · can rejoin until {klTime(s.rejoinBy)}
        </span>
      )}
    </>
  );
}

function PreJoin({
  s,
  canManage,
}: {
  s: StaffVideoSession;
  canManage: boolean;
}) {
  return (
    <>
      {s.blockers.length === 0 ? (
        <Badge tone="success">Ready</Badge>
      ) : (
        <ul className={styles.blockers} aria-label="Blocking the call">
          {s.blockers.map((b) => (
            <li key={b}>{BLOCKER_LABEL[b]}</li>
          ))}
        </ul>
      )}
      <span className={parity.sub}>
        {PAYMENT_LABEL[s.payment.state]}
        {s.payment.state === 'due' && ` · ${money(s.payment.dueSen)}`}
      </span>
      {canManage && s.payment.state === 'due' && <PayLaterButton s={s} />}
    </>
  );
}

function SessionsCard({ onJoin }: { onJoin: (id: string) => void }) {
  const { hasPermission } = useSession();
  const canManage = hasPermission('teleconsults:manage');
  const [date, setDate] = useState(todayKl);
  const [mine, setMine] = useState(true);
  const q = useVideoSessions(date, mine);
  return (
    <Card
      title="Video consults"
      flush
      actions={
        <div className={parity.rowActions}>
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={mine}
              onChange={(e) => setMine(e.target.checked)}
            />
            Only my sessions
          </label>
          <Input
            type="date"
            aria-label="Day"
            className={parity.headerControl}
            value={date}
            onChange={(e) => setDate(e.target.value || todayKl())}
          />
        </div>
      }
    >
      {q.data && !q.data.videoAvailable && (
        <p className={styles.warn} role="status">
          Video calls are switched off on this server: sessions are listed but
          calls cannot start.
        </p>
      )}
      <DataTable
        caption="Video sessions"
        rows={q.data?.rows}
        rowKey={(s) => s.id}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty={<EmptyState title="No video sessions on this day" />}
        columns={[
          { key: 't', header: 'Time', render: (s) => klTime(s.startsAt) },
          {
            key: 'p',
            header: 'Patient',
            render: (s) => (
              <>
                <Link to={`/patients/${s.patient.id}`}>
                  {s.patient.fullName}
                </Link>
                <span className={parity.sub}>
                  {TYPE_LABEL[s.type]}
                  {!s.mine && ` · ${s.doctor.fullName}`}
                </span>
              </>
            ),
          },
          {
            key: 's',
            header: 'Status',
            render: (s) => <SessionStatus s={s} />,
          },
          {
            key: 'b',
            header: 'Before the call',
            render: (s) => <PreJoin s={s} canManage={canManage} />,
          },
          {
            key: 'o',
            header: 'Outcome',
            render: (s) =>
              s.outcome ? (
                OUTCOME_LABEL[s.outcome.result]
              ) : s.outcomePending ? (
                <Badge tone="warning">Outcome needed</Badge>
              ) : (
                <span className={parity.sub}>–</span>
              ),
          },
          {
            key: 'x',
            header: '',
            render: (s) => (
              <JoinCell s={s} canManage={canManage} onJoin={onJoin} />
            ),
          },
        ]}
      />
    </Card>
  );
}

function JoinCell({
  s,
  canManage,
  onJoin,
}: {
  s: StaffVideoSession;
  canManage: boolean;
  onJoin: (id: string) => void;
}) {
  const over = !['scheduled', 'waiting', 'admitted', 'in_call'].includes(
    s.status,
  );
  if (over)
    return s.mine && canManage ? (
      <Button size="sm" onClick={() => onJoin(s.id)}>
        {s.outcomePending ? 'Record outcome' : 'Open'}
      </Button>
    ) : null;
  const reason = joinDisabledReason(s, canManage);
  const reasonId = `join-reason-${s.id}`;
  // Blocked only by a pre-join step: the session can still be opened
  // (pay later, cancel), just not joined from here.
  const canOpen =
    reason !== null && accessDisabledReason(s, canManage) === null;
  return (
    <>
      <div className={styles.inline}>
        <Button
          size="sm"
          variant="primary"
          disabled={reason !== null}
          aria-describedby={reason ? reasonId : undefined}
          onClick={() => onJoin(s.id)}
        >
          Join
        </Button>
        {canOpen && (
          <Button size="sm" onClick={() => onJoin(s.id)}>
            Open
          </Button>
        )}
      </div>
      {reason && (
        <span id={reasonId} className={styles.reason}>
          {reason}
        </span>
      )}
    </>
  );
}

type RequestDialog =
  | { kind: 'accept'; r: StaffVideoConsultRequest }
  | { kind: 'decline'; r: StaffVideoConsultRequest }
  | null;

function RequestsCard() {
  const { hasPermission } = useSession();
  const canManage = hasPermission('teleconsults:manage');
  const q = useVideoConsultRequests();
  const [open, setOpen] = useState<RequestDialog>(null);
  return (
    <Card title="Requests from the app" flush>
      <DataTable
        caption="Video consult requests from the app"
        rows={q.data?.rows}
        rowKey={(r) => r.id}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty={
          <EmptyState
            title="No open requests"
            body="Patients ask for a video consult in the app; requests expire after 4 hours."
          />
        }
        columns={[
          {
            key: 't',
            header: 'Asked',
            render: (r) => (
              <>
                {klTime(r.createdAt)}
                <span className={parity.sub}>
                  Expires {klTime(r.expiresAt)}
                </span>
              </>
            ),
          },
          {
            key: 'p',
            header: 'Patient',
            render: (r) => (
              <Link to={`/patients/${r.patient.id}`}>{r.patient.fullName}</Link>
            ),
          },
          {
            key: 'tr',
            header: 'Triage',
            render: (r) => <TriageSummary triage={r.triage} />,
          },
          {
            key: 'pay',
            header: 'Price',
            render: (r) => (
              <>
                {money(r.price.youPaySen)}
                <span className={parity.sub}>
                  {r.payer ? `${r.payer.name} pays` : 'Pays at the counter'}
                </span>
              </>
            ),
          },
          {
            key: 'x',
            header: '',
            render: (r) =>
              canManage ? (
                <div className={styles.inline}>
                  <Button
                    size="sm"
                    variant="primary"
                    aria-label={`Accept request from ${r.patient.fullName}`}
                    onClick={() => setOpen({ kind: 'accept', r })}
                  >
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    aria-label={`Decline request from ${r.patient.fullName}`}
                    onClick={() => setOpen({ kind: 'decline', r })}
                  >
                    Decline
                  </Button>
                </div>
              ) : null,
          },
        ]}
      />
      {open?.kind === 'accept' && (
        <AcceptRequestDialog r={open.r} onClose={() => setOpen(null)} />
      )}
      {open?.kind === 'decline' && (
        <DeclineRequestDialog r={open.r} onClose={() => setOpen(null)} />
      )}
    </Card>
  );
}
